// Пари в ЦЕЛИ центове — за аритметика без шума на float-а (бакшиш и всичко,
// което се праща обратно към BE като сума).
//
// BE праща `Decimal` като низ („12.50"). Низът се превръща в центове ВЕДНЪЖ,
// смята се с цели числа и се връща към BE като „x.xx" (`formatCents`).
// Закръглянето е ROUND_HALF_UP като на BE (`quantize(0.01, ROUND_HALF_UP)`):
// „2.675" → 268, „-2.675" → -268 (по модул, както Decimal).

const MONEY_PATTERN = /^\s*([+-])?(\d*)(?:[.,](\d*))?\s*$/;

/** Сума (низ от BE/поле или число) → центове; `null`, ако не е сума.
 *  Запетаята е десетичен знак като точката. Числото минава през своя запис
 *  („0.30000000000000004" → 30), не през двоичния си развой. */
export function parseMoneyToCents(
  value: string | number | null | undefined,
): number | null {
  if (value === null || value === undefined) return null;
  let text: string;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    text = String(value);
    // 1e-7 / 1e21 — експонентата не е запис на сума; 9 знака стигат за
    // закръглянето на стотинка.
    if (/e/i.test(text)) text = value.toFixed(9);
  } else {
    text = value;
  }
  const match = MONEY_PATTERN.exec(text);
  if (!match) return null;
  const [, sign, wholeDigits = "", fractionDigits = ""] = match;
  if (wholeDigits === "" && fractionDigits === "") return null;
  const whole = wholeDigits === "" ? 0 : Number(wholeDigits);
  const firstTwoDecimals = Number(`${fractionDigits}00`.slice(0, 2));
  const roundsUp = fractionDigits.length > 2 && Number(fractionDigits[2]) >= 5;
  const magnitude = whole * 100 + firstTwoDecimals + (roundsUp ? 1 : 0);
  if (!Number.isSafeInteger(magnitude)) return null;
  // Без -0: „-0.00" е просто нула.
  return sign === "-" && magnitude !== 0 ? -magnitude : magnitude;
}

/** Центове → „x.xx" (знакът отпред при отрицателна сума) — за тялото на
 *  заявката и за показване. */
export function formatCents(cents: number): string {
  const magnitude = Math.abs(Math.round(cents));
  const whole = Math.floor(magnitude / 100);
  const fraction = String(magnitude % 100).padStart(2, "0");
  return `${cents < 0 && magnitude !== 0 ? "-" : ""}${whole}.${fraction}`;
}

/** Дали низът е сума с най-много два знака след десетичния — без
 *  закръгляне (BE отказва „12.345" в настройките, не го закръгля). */
export function hasAtMostTwoDecimals(text: string): boolean {
  const match = MONEY_PATTERN.exec(text);
  if (!match) return false;
  const [, , wholeDigits = "", fractionDigits = ""] = match;
  if (wholeDigits === "" && fractionDigits === "") return false;
  return fractionDigits.length <= 2;
}

/** Писане на сума в поле: само цифри и ЕДИН десетичен знак (запетаята
 *  става точка), до 2 знака след него — както полето „Получено". */
export function sanitizeMoneyInput(raw: string): string {
  const value = raw.replace(/,/g, ".").replace(/[^0-9.]/g, "");
  const firstDot = value.indexOf(".");
  if (firstDot === -1) return value;
  const decimals = value
    .slice(firstDot + 1)
    .replace(/\./g, "")
    .slice(0, 2);
  return value.slice(0, firstDot + 1) + decimals;
}
